/**
 * Assessments Page
 *
 * Full list view of all repository assessments for the authenticated client.
 * Provides filtering, search, and detailed assessment information.
 *
 * Features:
 * - List all scanned repositories with health scores
 * - Filter by status (healthy, needs attention, at risk)
 * - Search by repository name
 * - Trigger workflows from assessment insights
 * - Navigate to detailed assessment view
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Badge,
  Spinner,
  Progress,
  Button,
  TextInput,
  Select,
  Table,
  Tooltip,
} from 'flowbite-react';
import {
  HiCheckCircle,
  HiXCircle,
  HiExclamation,
  HiExternalLink,
  HiRefresh,
  HiSearch,
  HiFilter,
  HiPlay,
  HiShieldCheck,
  HiDocumentSearch,
  HiLightningBolt,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../services/api/endpoints';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';

// =============================================================================
// TYPES
// =============================================================================

interface RecommendedAction {
  type: string;
  label: string;
  priority: 'high' | 'medium' | 'low';
  workflow: string;
  description: string;
}

interface Assessment {
  Assessment_ID: string;
  Repository: string;
  Status: string;
  Created_At: string;
  Completed_At: string;
  Health_Score: number;
  File_Count: number;
  Primary_Language: string;
  Frameworks: string[];
  Has_Tests: boolean;
  Has_CI: boolean;
  Has_Docker: boolean;
  Has_Infrastructure: boolean;
  Insights_Count: number;
  Concerns_Count: number;
  Top_Concerns: string[];
  GitHub_Issue: {
    Number: number;
    URL: string;
  } | null;
  Project: {
    ID: string;
    Name: string;
  } | null;
  Company: {
    ID: string;
    Name: string;
  } | null;
  Recommended_Actions: RecommendedAction[];
}

type HealthFilter = 'all' | 'healthy' | 'attention' | 'risk';

// =============================================================================
// COMPONENT
// =============================================================================

const AssessmentsPage: React.FC = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('all');

  // Get client ID from localStorage
  const clientId = localStorage.getItem('selectedClientId') || '';

  const fetchAssessments = useCallback(async () => {
    if (!clientId) {
      setError('No client context selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<Assessment>(
        API_ENDPOINTS.GITHUB_APP.ASSESSMENTS_LIST,
        'GET',
        undefined,
        { params: { Client_ID: clientId, limit: '100' } }
      );

      if (isSuccessResponse(response)) {
        const fetchedAssessments = response.data.Records || [];
        setAssessments(fetchedAssessments);
      } else {
        setError(response.message || 'Failed to load assessments');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load assessments';
      setError(errorMessage);
      console.error('[AssessmentsPage] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // =============================================================================
  // FILTERS
  // =============================================================================

  const filteredAssessments = useMemo(() => {
    return assessments.filter((assessment) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          assessment.Repository.toLowerCase().includes(query) ||
          assessment.Primary_Language.toLowerCase().includes(query) ||
          assessment.Project?.Name.toLowerCase().includes(query) ||
          assessment.Company?.Name.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Health filter
      if (healthFilter !== 'all') {
        const score = assessment.Health_Score;
        switch (healthFilter) {
          case 'healthy':
            if (score < 70) return false;
            break;
          case 'attention':
            if (score < 40 || score >= 70) return false;
            break;
          case 'risk':
            if (score >= 40) return false;
            break;
        }
      }

      return true;
    });
  }, [assessments, searchQuery, healthFilter]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const handleTriggerWorkflow = useCallback(async (workflow: string, repository: string) => {
    toast.success(`Starting ${workflow} workflow for ${repository}...`);
    console.log('[AssessmentsPage] Trigger workflow:', { workflow, repository });

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.AGENTS.EXECUTE,
        'POST',
        {
          workflow,
          repository,
          Client_ID: clientId,
        }
      );

      if (isSuccessResponse(response)) {
        toast.success(`${workflow} workflow started successfully`);
      } else {
        toast.error(response.message || `Failed to start ${workflow} workflow`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start workflow';
      toast.error(errorMessage);
      console.error('[AssessmentsPage] Workflow execution error:', err);
    }
  }, [clientId]);

  // =============================================================================
  // HELPERS
  // =============================================================================

  const getHealthColor = (score: number): string => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  const getHealthLabel = (score: number): string => {
    if (score >= 70) return 'Healthy';
    if (score >= 40) return 'Needs Attention';
    return 'At Risk';
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'failure';
      case 'medium': return 'warning';
      default: return 'gray';
    }
  };

  const getWorkflowIcon = (workflow: string) => {
    switch (workflow) {
      case 'security-review':
        return <HiShieldCheck className="w-4 h-4" />;
      case 'quality-check':
        return <HiDocumentSearch className="w-4 h-4" />;
      case 'deploy-feature':
        return <HiLightningBolt className="w-4 h-4" />;
      default:
        return <HiPlay className="w-4 h-4" />;
    }
  };

  // =============================================================================
  // STATS
  // =============================================================================

  const stats = useMemo(() => {
    const healthy = assessments.filter(a => a.Health_Score >= 70).length;
    const attention = assessments.filter(a => a.Health_Score >= 40 && a.Health_Score < 70).length;
    const risk = assessments.filter(a => a.Health_Score < 40).length;
    const avgScore = assessments.length > 0
      ? Math.round(assessments.reduce((sum, a) => sum + a.Health_Score, 0) / assessments.length)
      : 0;

    return { healthy, attention, risk, avgScore, total: assessments.length };
  }, [assessments]);

  // =============================================================================
  // RENDER: LOADING
  // =============================================================================

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Repository Assessments</h1>
        <div className="flex items-center justify-center py-16">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ERROR
  // =============================================================================

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Repository Assessments</h1>
        <Card>
          <div className="text-center py-8">
            <HiExclamation className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Assessments</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button color="blue" onClick={fetchAssessments}>
              <HiRefresh className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // =============================================================================
  // RENDER: MAIN
  // =============================================================================

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Repository Assessments</h1>
            <p className="text-gray-600">Manage and review all scanned repositories</p>
          </div>
          <div className="flex gap-3">
            <Button color="gray" onClick={fetchAssessments}>
              <HiRefresh className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Link to="/settings/github-app">
              <Button color="blue">
                <FaGithub className="w-4 h-4 mr-2" />
                Connect Repository
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Scanned</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600">{stats.avgScore}</div>
              <div className="text-sm text-gray-500">Avg Health</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{stats.healthy}</div>
              <div className="text-sm text-gray-500">Healthy</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats.attention}</div>
              <div className="text-sm text-gray-500">Needs Attention</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{stats.risk}</div>
              <div className="text-sm text-gray-500">At Risk</div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <TextInput
                icon={HiSearch}
                placeholder="Search repositories, projects, languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="w-48">
              <Select
                icon={HiFilter}
                value={healthFilter}
                onChange={(e) => setHealthFilter(e.target.value as HealthFilter)}
              >
                <option value="all">All Status</option>
                <option value="healthy">Healthy (70+)</option>
                <option value="attention">Needs Attention (40-69)</option>
                <option value="risk">At Risk (&lt;40)</option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Assessments Table */}
        {filteredAssessments.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <FaGithub className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {assessments.length === 0 ? 'No Repositories Scanned' : 'No Matching Results'}
              </h2>
              <p className="text-gray-600 mb-6">
                {assessments.length === 0
                  ? 'Connect a GitHub repository to start scanning'
                  : 'Try adjusting your search or filter criteria'}
              </p>
              {assessments.length === 0 && (
                <Link to="/settings/github-app">
                  <Button color="blue">
                    <FaGithub className="w-4 h-4 mr-2" />
                    Connect Repository
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>Repository</Table.HeadCell>
                  <Table.HeadCell>Health</Table.HeadCell>
                  <Table.HeadCell>Infrastructure</Table.HeadCell>
                  <Table.HeadCell>Key Concerns</Table.HeadCell>
                  <Table.HeadCell>Actions</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {filteredAssessments.map((assessment) => (
                    <Table.Row key={assessment.Assessment_ID} className="bg-white">
                      {/* Repository Info */}
                      <Table.Cell>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <FaGithub className="w-4 h-4 text-gray-500" />
                            <a
                              href={`https://github.com/${assessment.Repository}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-gray-900 hover:text-indigo-600"
                            >
                              {assessment.Repository}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Badge color="gray" size="xs">{assessment.Primary_Language}</Badge>
                            <span>{assessment.File_Count.toLocaleString()} files</span>
                            {assessment.Project && (
                              <span>| {assessment.Project.Name}</span>
                            )}
                          </div>
                        </div>
                      </Table.Cell>

                      {/* Health Score */}
                      <Table.Cell>
                        <div className="w-24">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-lg font-bold ${
                              assessment.Health_Score >= 70 ? 'text-green-600' :
                              assessment.Health_Score >= 40 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {assessment.Health_Score}
                            </span>
                            <Badge color={getHealthColor(assessment.Health_Score)} size="xs">
                              {getHealthLabel(assessment.Health_Score)}
                            </Badge>
                          </div>
                          <Progress
                            progress={assessment.Health_Score}
                            color={getHealthColor(assessment.Health_Score)}
                            size="sm"
                          />
                        </div>
                      </Table.Cell>

                      {/* Infrastructure */}
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          <Tooltip content="Test Suite">
                            <Badge color={assessment.Has_Tests ? 'green' : 'gray'} size="xs">
                              {assessment.Has_Tests ? <HiCheckCircle className="w-3 h-3" /> : <HiXCircle className="w-3 h-3" />}
                              <span className="ml-1">Tests</span>
                            </Badge>
                          </Tooltip>
                          <Tooltip content="CI/CD Pipeline">
                            <Badge color={assessment.Has_CI ? 'green' : 'gray'} size="xs">
                              {assessment.Has_CI ? <HiCheckCircle className="w-3 h-3" /> : <HiXCircle className="w-3 h-3" />}
                              <span className="ml-1">CI</span>
                            </Badge>
                          </Tooltip>
                          <Tooltip content="Docker">
                            <Badge color={assessment.Has_Docker ? 'green' : 'gray'} size="xs">
                              {assessment.Has_Docker ? <HiCheckCircle className="w-3 h-3" /> : <HiXCircle className="w-3 h-3" />}
                              <span className="ml-1">Docker</span>
                            </Badge>
                          </Tooltip>
                        </div>
                      </Table.Cell>

                      {/* Key Concerns */}
                      <Table.Cell>
                        {assessment.Top_Concerns.length > 0 ? (
                          <ul className="text-xs text-gray-600 space-y-0.5 max-w-xs">
                            {assessment.Top_Concerns.slice(0, 2).map((concern, i) => (
                              <li key={i} className="truncate">- {concern}</li>
                            ))}
                            {assessment.Top_Concerns.length > 2 && (
                              <li className="text-gray-400">+{assessment.Top_Concerns.length - 2} more</li>
                            )}
                          </ul>
                        ) : (
                          <span className="text-xs text-gray-400">No concerns</span>
                        )}
                      </Table.Cell>

                      {/* Actions */}
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          {assessment.Recommended_Actions.slice(0, 2).map((action) => (
                            <Tooltip key={action.type} content={action.description}>
                              <Button
                                size="xs"
                                color={getPriorityColor(action.priority)}
                                onClick={() => handleTriggerWorkflow(action.workflow, assessment.Repository)}
                                outline={action.priority !== 'high'}
                              >
                                {getWorkflowIcon(action.workflow)}
                              </Button>
                            </Tooltip>
                          ))}
                          <Link to={`/assessment/${encodeURIComponent(assessment.Repository)}`}>
                            <Button size="xs" color="gray">
                              <HiExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                          {assessment.GitHub_Issue && (
                            <a href={assessment.GitHub_Issue.URL} target="_blank" rel="noopener noreferrer">
                              <Button size="xs" color="gray">
                                <FaGithub className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            {/* Results count */}
            <div className="text-xs text-gray-500 text-right mt-4">
              Showing {filteredAssessments.length} of {assessments.length} assessments
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AssessmentsPage;
